import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { BackButton } from "@/common/BackButton";
import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomSingleSelect,
  CustomCalendarInput,
  CustomSectionTitle,
  CustomCategorySelectSingle,
  CustomCascadeSelect,
  CustomLoading,
  FixedActionBar,
  useScrollToFirstError,
  CustomFormNavigationGuard,
  usePreviewDrawerUnsaved,
  type CategoryOption,
} from "@ippon/shared-components";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchPaymentById,
  updatePayment,
  createPayment,
  createPropertyPayment,
  deletePayment,
} from "@/store/paymentSlice";
import { fetchUnitsForRentRoll } from "@/store/unitSlice";
import ipponApi from "@/config/axios";
import { Can } from "@/components/auth/Can";
import {
  fetchPaymentOptionManagement,
  upsertPaymentOptionManagement,
} from "@/store/paymentOptionManagementSlice";
import {
  fetchBudgetCategories,
  createBudgetCategory,
} from "@/store/budgetSlice";
import { AREA_TYPE_LABELS } from "@ippon/shared-types";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { applyApiError } from "@/utils/applyApiError";
import { useRefetchOn } from "@/hooks/useRefetchOn";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import BillingItemsTable, {
  type BillingItemRow,
  hasBillingAmountError,
} from "@/components/PropertyDetail/shared/BillingItemsTable";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import { DEFAULT_STATUSES } from "@/components/PropertyDetail/shared/PaymentDrawer";

interface Props {
  /** PreviewDrawer 等で埋め込む際に使用する payment_id (URL params より優先) */
  overridePaymentId?: string;
  /** PreviewDrawer 経由の section_id 指定 (querystring より優先) */
  overridePropertySectionId?: string;
  /** PreviewDrawer 経由の property_id 指定 (querystring より優先)。
   *  「その他」収入 (Unit 非紐付け) の取得フォールバックに使う。 */
  overridePropertyId?: string;
  /** 収入サブタブ起点。true で「カテゴリ→場所区分→名称」欄を表示・編集可にする
   *  (テナント入金管理詳細では渡さない = 表示されない)。 */
  incomeContext?: boolean;
  embedded?: boolean;
  /** ドロワー内で「戻る」を出す場合のハンドラ (指定時は embedded でもタイトルに戻るアイコン表示)。
   *  区画プレビュードロワー内でコンテンツ切替した際、元のビューに戻すために使う。 */
  onBack?: () => void;
  /** 編集成功後コールバック (親で一覧再取得) */
  onUpdated?: (paymentId: string) => void;
  /** ロード成功時に呼ばれる (ドロワー埋め込み時にタイトル等の取得に使う) */
  onLoaded?: (payment: any) => void;
}

/** revenue_group コード → 表示ラベル (請求明細テーブルの「カテゴリ」列用) */
const REVENUE_GROUP_LABEL: Record<string, string> = {
  stock_rent: "貸室",
  stock_other: "付帯",
  other: "その他",
};

interface FormValues {
  status: string;
  title: string;
  aggregation_month: string;
  scheduled_date: string;
  transfer_datetime: string;
  transfer_method: "口座振替" | "顧客振込" | "";
  remarks: string;
}

const LABEL_WIDTH = "130px";
const INPUT_WIDTH = "600px";
// 明細テーブル + 合計のみ広げる (基本情報フィールドは INPUT_WIDTH=600 のまま)。
const BILLING_WIDTH = "860px";

/**
 * 請求明細を「内容のみ(item_id は除外)」で安定文字列化。
 * 更新ボタンの活性判定で、初期スナップショットと現在値を比較するのに使う。
 * item_id は生成値で内容と無関係なので除外し、順序は保持する。
 */
const serializeBillingItems = (rows: BillingItemRow[]): string =>
  JSON.stringify(
    rows.map((r) => [
      r.category_id || "",
      (r.name || "").trim(),
      String(r.amount ?? ""),
      r.tax_category || "non_taxable",
    ]),
  );

const TRANSFER_METHODS = [
  { value: "口座振替", label: "口座振替" },
  { value: "顧客振込", label: "顧客振込" },
];

/** 請求年月の選択肢: 現在月を中央に過去30月 + 未来30月。PaymentDrawer と同じ。 */
const billingMonthOptions = (() => {
  const now = new Date();
  const opts: { value: string; label: string }[] = [];
  for (let offset = 30; offset >= -30; offset--) {
    const d = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    opts.push({ value: `${y}-${m}`, label: `${y}年${m}月` });
  }
  return opts;
})();

export const PaymentDetailPage: React.FC<Props> = ({
  overridePaymentId,
  overridePropertySectionId,
  overridePropertyId,
  incomeContext,
  embedded,
  onBack,
  onUpdated,
  onLoaded,
}) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const paymentId = overridePaymentId || params?.id || "";
  const propertySectionId =
    overridePropertySectionId || searchParams.get("property_section_id") || "";
  // 「その他」収入 (Unit 非紐付け) は section で引けないため propertyId をフォールバックに渡す
  const propertyId =
    overridePropertyId || searchParams.get("property_id") || "";
  // 収入文脈: prop or ?context=income (全画面リロードでも維持されるよう query も見る)
  const incomeMode =
    Boolean(incomeContext) || searchParams.get("context") === "income";
  const navigationState = (location.state as Record<string, unknown>) || {};
  const backPath =
    typeof navigationState.backPath === "string"
      ? navigationState.backPath
      : "";
  const handleHeaderBack = useCallback(() => {
    if (backPath) {
      navigate(backPath, { replace: true });
      return;
    }
    navigate(-1);
  }, [backPath, navigate]);

  const [data, setData] = useState<any | null>(null);
  const dataRef = useRef<any | null>(null);
  const [breadcrumbPropertyName, setBreadcrumbPropertyName] = useState(
    () => (navigationState.propName as string | undefined) || "",
  );
  const [breadcrumbUnitName, setBreadcrumbUnitName] = useState(
    () => (navigationState.privateAreaName as string | undefined) || "",
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );

  // 内訳 (RHF 外で管理 — PaymentDrawer と同じ方針)。請求明細テーブル (BillingItemsTable) で編集。
  const [itemEntries, setItemEntries] = useState<BillingItemRow[]>([]);
  const [itemError, setItemError] = useState(false);
  // 科目(明細項目)未選択の行 item_id。支出側の「金額は必須」と同様、
  // 該当行の科目セレクト直下にインラインで必須エラーを出す。
  const [categoryRequiredErrorItemIds, setCategoryRequiredErrorItemIds] =
    useState<Set<string>>(() => new Set());
  const [statusError, setStatusError] = useState(false);
  const [localDirty, setLocalDirty] = useState(false);
  // 更新ボタンの活性判定用: 「クリーン状態(読込/保存直後)」の明細スナップショット。
  // 現在の明細と内容比較し、元に戻れば非活性・実際に変われば活性、と正しく追従させる。
  const initialItemsSerializedRef = useRef<string>(serializeBillingItems([]));
  const itemsDirty =
    serializeBillingItems(itemEntries) !== initialItemsSerializedRef.current;

  // ステータス追加候補 (BE 永続済 + ローカル追加)
  const [statusOptions, setStatusOptions] =
    useState<CategoryOption[]>(DEFAULT_STATUSES);
  const optionRecord = useSelector(
    (s: RootState) => (s as any).paymentOptionManagement.record,
  );

  const budgetCategories = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.data || [],
  );

  // 収入カテゴリ (システム標準) を選択肢として利用
  const incomeCategoryOptions = useMemo(() => {
    if (!Array.isArray(budgetCategories)) return [];
    return (
      budgetCategories
        // 収益カテゴリで絞らず全科目 (明細フリー)。新規登録したユーザー科目も出すため is_system 不問。
        .filter((c: any) => c.category_type === "income" && !c.deleted_at)
        .map((c: any) => ({
          category_id: String(c.category_id),
          name: String(c.name),
        }))
    );
  }, [budgetCategories]);

  const {
    control,
    reset,
    watch,
    setValue,
    getValues,
    setError,
    setFocus,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      status: "",
      title: "",
      aggregation_month: "",
      scheduled_date: "",
      transfer_datetime: "",
      transfer_method: "",
      remarks: "",
    },
  });

  const statusValue = watch("status");

  // PreviewDrawer 内で開かれている場合、閉じ時の破棄確認用に dirty を報告 (外では no-op)。
  usePreviewDrawerUnsaved(isDirty || localDirty || itemsDirty);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // onLoaded を ref 経由で呼び、fetchDetail useCallback の deps から外す。
  // 親インライン関数で毎レンダー new ref になっても fetch ループにならないように。
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  // ステータス候補を取得 (BE 永続済を DEFAULT_STATUSES とマージ)
  useEffect(() => {
    dispatch(fetchPaymentOptionManagement())
      .unwrap()
      .then((rec: any) => {
        if (!rec || !Array.isArray(rec.statuses)) return;
        const merged = Array.from(
          new Set([...DEFAULT_STATUSES.map((o) => o.value), ...rec.statuses]),
        );
        setStatusOptions(
          merged.map((v) => ({ label: String(v), value: String(v) })),
        );
      })
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!optionRecord || !Array.isArray(optionRecord.statuses)) return;
    const merged = Array.from(
      new Set([
        ...DEFAULT_STATUSES.map((o) => o.value),
        ...optionRecord.statuses,
      ]),
    );
    setStatusOptions(
      merged.map((v) => ({ label: String(v), value: String(v) })),
    );
  }, [optionRecord]);

  // 入金内訳の候補 (収入カテゴリ) 取得
  useEffect(() => {
    dispatch(fetchBudgetCategories() as any);
  }, [dispatch]);

  // ── 収入文脈のみ: カテゴリ → 場所区分 → 名称 (収入登録モーダルと同じ内容) ──
  // incomeMode=false (テナント入金詳細) では描画も保存ロジックも一切走らない。
  const categoryRevenueGroup = useMemo(() => {
    const map: Record<string, string> = {};
    (budgetCategories as any[]).forEach((c) => {
      if (c && c.category_type === "income" && c.revenue_group) {
        map[c.category_id] = c.revenue_group;
      }
    });
    return map;
  }, [budgetCategories]);

  const [incomeCategory, setIncomeCategory] = useState(""); // revenue_group
  const [incomeAreaType, setIncomeAreaType] = useState("");
  const [incomeUnitId, setIncomeUnitId] = useState("");
  const [incomeUnits, setIncomeUnits] = useState<any[]>([]);

  // 物件配下の収益区画 (貸室/その他収入) を取得。
  // 全画面の直リンク時はパンくずの区画名復元にも使う。
  useEffect(() => {
    if (!propertyId) return;
    dispatch(fetchUnitsForRentRoll({ property_id: propertyId }) as any)
      .unwrap()
      .then((list: any[]) => setIncomeUnits(Array.isArray(list) ? list : []))
      .catch(() => setIncomeUnits([]));
  }, [propertyId, dispatch]);

  useEffect(() => {
    if (!propertyId || breadcrumbPropertyName || embedded) return;
    let cancelled = false;
    ipponApi
      .get(`${import.meta.env.VITE_API_URL}/api/v1/properties/${propertyId}`)
      .then((response) => {
        const name = response?.data?.data?.name;
        if (!cancelled && typeof name === "string") {
          setBreadcrumbPropertyName(name);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [breadcrumbPropertyName, embedded, propertyId]);

  useEffect(() => {
    if (breadcrumbUnitName) return;
    const unitId =
      data?.unit_id ||
      (propertySectionId && propertySectionId !== propertyId
        ? propertySectionId
        : "");
    if (!unitId) return;
    const unit = incomeUnits.find((candidate) => candidate?.unit_id === unitId);
    if (unit?.landmark_name) {
      setBreadcrumbUnitName(unit.landmark_name);
    }
  }, [
    breadcrumbUnitName,
    data?.unit_id,
    incomeUnits,
    propertyId,
    propertySectionId,
  ]);

  // 収益カテゴリ (revenue_group) は明細の先頭科目から導出するだけ (ユーザーは
  // カスケードで編集しない)。categoryRevenueGroup が遅れて読み込まれても追従させる。
  useEffect(() => {
    if (!incomeMode || !data) return;
    const items: any[] = Array.isArray(data.items) ? data.items : [];
    const firstCatId = items.length > 0 ? items[0]?.category_id : "";
    const rg = firstCatId
      ? categoryRevenueGroup[firstCatId] || "other"
      : "other";
    setIncomeCategory(rg);
  }, [incomeMode, data, categoryRevenueGroup]);

  // 場所(区画)・場所区分の初期値は「読み込んだレコードごとに1回だけ」反映する。
  // タブ切替等で data/categoryRevenueGroup の参照が変わっても、ユーザーがカスケードで
  // 選んだローカル値を上書きしない (保存時はその選択値で更新登録される)。
  const incomeSelectionInitForIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!incomeMode || !data) return;
    const recId = paymentId || (data.sort_key as string) || "";
    if (incomeSelectionInitForIdRef.current === recId) return;
    incomeSelectionInitForIdRef.current = recId;
    setIncomeAreaType(data.unit_area_type_snapshot || "");
    setIncomeUnitId(data.unit_id || "");
  }, [incomeMode, data, paymentId]);

  // 場所区分(area)の補完: 区画(unit_id)はあるのに場所区分が空のとき、区画一覧から
  // area_type を引いて埋める。保存経路で unit_area_type_snapshot を送っていないため、
  // 再読込時に snapshot が空でもカスケード(場所/テナント)が正しく表示されるようにする。
  useEffect(() => {
    if (!incomeMode) return;
    if (!incomeUnitId || incomeAreaType) return;
    const u = (incomeUnits as any[]).find((x) => x?.unit_id === incomeUnitId);
    if (u?.area_type) setIncomeAreaType(u.area_type as string);
  }, [incomeMode, incomeUnitId, incomeAreaType, incomeUnits]);

  // 区画→現契約者(テナント)。逆引き・他区画表示用に物件配下の active 契約を引く。
  const [tenantByUnitId, setTenantByUnitId] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    if (!incomeMode || !propertyId) return;
    let cancelled = false;
    ipponApi
      .get(`${import.meta.env.VITE_API_URL}/api/v1/contracts`, {
        params: { property_id: propertyId, status: "active" },
      })
      .then((res) => {
        if (cancelled) return;
        const items = (res.data?.data?.items || []) as any[];
        const byUnit: Record<string, any> = {};
        for (const c of items) {
          const uid = c?.unit_id;
          if (!uid) continue;
          const prev = byUnit[uid];
          if (
            !prev ||
            new Date(c.created_at ?? 0).getTime() >
              new Date(prev.created_at ?? 0).getTime()
          ) {
            byUnit[uid] = c;
          }
        }
        const map: Record<string, string> = {};
        Object.keys(byUnit).forEach((uid) => {
          map[uid] = String(byUnit[uid]?.contractor_name || "");
        });
        setTenantByUnitId(map);
      })
      .catch(() => setTenantByUnitId({}));
    return () => {
      cancelled = true;
    };
  }, [incomeMode, propertyId]);

  // 区画(unit)を選んでいれば区画紐付け、未選択なら property レベル(非紐付け)収入。
  const incomeUnitLinked = Boolean(incomeUnitId);

  // カスケード = 区画への任意リンク専用。場所区分の選択肢 = 区画の area_type (distinct)。
  // 「その他(区画なし)」は廃止 — 区画未選択で property レベル、収益カテゴリは明細から決まる。
  const incomeAreaOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const u of incomeUnits as any[]) {
      if (!u || u.deleted_at) continue;
      const at = String(u.area_type || "");
      if (at && !seen.has(at)) {
        seen.set(at, (AREA_TYPE_LABELS as any)[at] || at);
      }
    }
    return Array.from(seen, ([v, label]) => ({ value: v, label }));
  }, [incomeUnits]);

  // カスケード行 = 区画。記録レコードの区画はテナント=請求年月 snapshot (案C-b)、
  // 他区画は現契約者(ライブ)。→ 初期表示は記録された契約者、区画変更で新区画の現契約者。
  const incomeCascadeRows = useMemo(
    () =>
      (incomeUnits as any[])
        .filter((u) => u && !u.deleted_at)
        .map((u) => ({
          area: u.area_type as string,
          area_label: (AREA_TYPE_LABELS as any)[u.area_type] || u.area_type,
          name: u.unit_id as string,
          name_label: u.landmark_name || u.unit_id,
          tenant:
            u.unit_id === (data as any)?.unit_id
              ? (data as any)?.contractor_name_snapshot ||
                tenantByUnitId[u.unit_id] ||
                null
              : tenantByUnitId[u.unit_id] || null,
        })),
    [incomeUnits, tenantByUnitId, data],
  );

  const fetchDetail = useCallback(async () => {
    setInitialLoadError(null);
    if (!paymentId) return;
    if (!propertySectionId) {
      // URL から section が引けない (直リン等) → notFound 扱い
      setInitialLoadError("notFound");
      return;
    }
    setLoading(true);
    try {
      const rec = await dispatch(
        fetchPaymentById({ propertySectionId, paymentId, propertyId }),
      ).unwrap();
      setData(rec);
      onLoadedRef.current?.(rec);

      const items: BillingItemRow[] = Array.isArray(rec.items)
        ? rec.items.map((i: any, idx: number) => ({
            item_id: `item_${idx}_${Date.now()}`,
            category_id: i.category_id || "",
            name: i.name || "",
            amount: i.amount != null ? String(i.amount) : "",
            tax_category:
              i.tax_category === "taxable" ? "taxable" : "non_taxable",
          }))
        : [];

      setItemEntries(items);
      // クリーン状態の明細スナップショットを更新 (以降はこの内容と比較して活性判定)。
      initialItemsSerializedRef.current = serializeBillingItems(items);

      reset({
        status: rec.status || "",
        title: rec.title || "",
        aggregation_month: rec.aggregation_month || "",
        scheduled_date: rec.scheduled_date || "",
        transfer_datetime: rec.transfer_datetime || "",
        transfer_method: rec.transfer_method || "",
        remarks: rec.remarks || "",
      });
      setLocalDirty(false);
      setItemError(false);
      setCategoryRequiredErrorItemIds(new Set());
      setStatusError(false);
    } catch (err) {
      const payload = err as ApiErrorPayload | undefined;
      const errorType = payload?.errorType ?? "unknown";
      setInitialLoadError(errorType);
      if (errorType === "notFound" || !dataRef.current) {
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, paymentId, propertySectionId, propertyId, reset]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // AI チャットアクション完了時 (refetchBus.emit('payment:<id>')) に再取得。
  useRefetchOn("payment", paymentId, fetchDetail);

  // パンくず最終項目に請求タイトル (= 請求年月) を反映
  const lastBreadcrumbStateRef = useRef<string>("");
  useEffect(() => {
    if (embedded) return;
    const month = data?.aggregation_month;
    if (!month) return;
    const title = /^\d{4}-\d{2}$/.test(month)
      ? `${month.slice(0, 4)}年${month.slice(5, 7)}月`
      : month;
    const unitId =
      data?.unit_id ||
      (propertySectionId && propertySectionId !== propertyId
        ? propertySectionId
        : "");
    const stateKey = `${title}|${breadcrumbPropertyName}|${unitId}|${breadcrumbUnitName}`;
    if (stateKey === lastBreadcrumbStateRef.current) return;
    lastBreadcrumbStateRef.current = stateKey;
    const currentState = (location.state as Record<string, unknown>) || {};
    navigate(`/payments/${paymentId}${location.search || ""}`, {
      state: {
        ...currentState,
        title,
        propName: breadcrumbPropertyName || currentState.propName,
        privateAreaId: unitId || undefined,
        privateAreaName: breadcrumbUnitName || undefined,
      },
      replace: true,
    });
  }, [
    data?.aggregation_month,
    paymentId,
    embedded,
    navigate,
    location.state,
    location.search,
    data?.unit_id,
    propertySectionId,
    propertyId,
    breadcrumbPropertyName,
    breadcrumbUnitName,
  ]);

  // 請求明細テーブル (BillingItemsTable) の onChange。内訳の追加/編集/削除/並べ替えを一括で受ける。
  // 科目(明細項目)が未選択か。既存科目(category_id)も新規入力中(isNew+name)も無ければ未選択。
  const isItemCategoryMissing = (row: BillingItemRow): boolean =>
    !(row.category_id || (row.isNew && (row.name || "").trim() !== ""));

  const getCategoryRequiredErrorItemIds = (
    rows: BillingItemRow[],
  ): Set<string> =>
    new Set(rows.filter(isItemCategoryMissing).map((r) => r.item_id));

  const handleItemsChange = useCallback((next: BillingItemRow[]) => {
    setItemEntries(next);
    setItemError(false);
    // 既にエラー表示中なら、入力に追従して即時に再計算 (解消した行は赤を消す)。
    setCategoryRequiredErrorItemIds((prev) =>
      prev.size > 0 ? getCategoryRequiredErrorItemIds(next) : prev,
    );
    // 明細の dirty は itemsDirty(スナップショット比較)で判定するため、ここでは
    // localDirty を立てない (元に戻したら非活性へ戻すため)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 科目セレクトの「カテゴリ」列表示用 + 新規追加。revenue_group は導出 (categoryRevenueGroup)。
  const billingCategoryOptions = useMemo(
    () =>
      incomeCategoryOptions.map((c) => {
        const rg = categoryRevenueGroup[c.category_id];
        return {
          value: c.category_id,
          label: c.name,
          // 固定3つはラベル、カスタム収益カテゴリは rg(=名称) をそのまま
          categoryLabel: REVENUE_GROUP_LABEL[rg] || rg || "",
        };
      }),
    [incomeCategoryOptions, categoryRevenueGroup],
  );

  // 収益カテゴリ選択肢 (新規登録モーダル用): 固定3つ + 既存カスタム distinct。
  const revenueGroupOptions = useMemo(() => {
    const base = [
      { value: "stock_rent", label: "貸室" },
      { value: "stock_other", label: "付帯" },
      { value: "other", label: "その他" },
    ];
    const seen = new Set(base.map((b) => b.value));
    for (const cid of Object.keys(categoryRevenueGroup)) {
      const rg = categoryRevenueGroup[cid];
      if (rg && !seen.has(rg)) {
        seen.add(rg);
        base.push({ value: rg, label: REVENUE_GROUP_LABEL[rg] || rg });
      }
    }
    return base;
  }, [categoryRevenueGroup]);

  // 科目の新規追加: 選択中カテゴリ (revenue_group) で BudgetCategory を作成し category_id を返す。
  // モーダル(明細セレクト新規登録)からは (name, revenueGroup) で、保存時 isNew 行からは name のみ
  // (収益カテゴリは選択中=incomeCategory にフォールバック) で呼ぶ。即時作成→一覧再取得。
  const handleAddCategory = useCallback(
    async (
      name: string,
      revenueGroup?: string,
    ): Promise<string | undefined> => {
      const trimmed = name.trim();
      if (!trimmed) return undefined;
      try {
        const created: any = await dispatch(
          createBudgetCategory({
            name: trimmed,
            category_type: "income",
            revenue_group: (revenueGroup || incomeCategory || "other") as
              | "stock_rent"
              | "stock_other"
              | "other",
          }) as any,
        ).unwrap();
        await dispatch(fetchBudgetCategories() as any);
        return (created?.category_id as string) || undefined;
      } catch {
        showToast({ type: "error", message: "科目の追加に失敗しました" });
        return undefined;
      }
    },
    [dispatch, incomeCategory, showToast],
  );

  const handleAddNewStatus = useCallback(
    (newStatus: string) => {
      const trimmed = newStatus.trim();
      if (!trimmed) return;
      const exists = statusOptions.some(
        (o) => o.value.toLowerCase() === trimmed.toLowerCase(),
      );
      if (exists) return;
      setStatusOptions((prev) => [...prev, { label: trimmed, value: trimmed }]);
    },
    [statusOptions],
  );

  const scrollToFirstError = useScrollToFirstError<FormValues>();
  // 明細必須エラー時のスクロール先 (請求明細テーブル)。
  const billingTableRef = useRef<HTMLDivElement>(null);
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;

    const values = getValues();
    const hasStatusError = !values.status || !values.status.trim();
    const hasItemError = itemEntries.length === 0;
    // 科目(明細項目)未選択の行を検出。1行でもあれば送信を止め、該当行の科目セレクトに
    // インラインで必須エラーを出す (API の「支払い項目は1件以上指定してください」を未然に防ぐ)。
    const categoryMissingIds = getCategoryRequiredErrorItemIds(itemEntries);
    // 金額の桁数オーバー (12桁以上) は BillingItemsTable 側が赤字表示。送信は止める。
    const hasAmountError = hasBillingAmountError(itemEntries);
    setStatusError(hasStatusError);
    setItemError(hasItemError);
    setCategoryRequiredErrorItemIds(categoryMissingIds);
    if (
      hasStatusError ||
      hasItemError ||
      hasAmountError ||
      categoryMissingIds.size > 0
    ) {
      // DOM 更新(エラー文表示)後にスクロール。明細エラーはテーブルへ直接スクロールする
      // (ステータスは sticky ヘッダー内で常時表示=スクロール不要)。
      requestAnimationFrame(() => {
        if (hasItemError || hasAmountError || categoryMissingIds.size > 0) {
          billingTableRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else {
          scrollToFirstError({ status: { message: "必須" } } as any, e);
        }
      });
      return;
    }

    // 区画(カスケード)は任意リンク。未選択でも property レベル収入として保存可。
    // 収益カテゴリは明細(科目)から決まるため、区画/カテゴリの必須検証は無し。

    setSubmitting(true);
    try {
      // 科目未選択の空行は保存しない (予実集計でスキップされ請求額と不整合になるため)。
      // ローカル新規追加 (isNew) は保存時にここで BudgetCategory を永続化し category_id を確定。
      const itemsPayload = await Promise.all(
        itemEntries
          .filter((i) => i.category_id || (i.isNew && i.name.trim()))
          .map(async (i) => {
            let categoryId = i.category_id;
            if ((i.isNew || !categoryId) && i.name.trim()) {
              categoryId = (await handleAddCategory(i.name)) || "";
            }
            const amount = i.amount ? parseFloat(i.amount) : 0;
            const taxRate = i.tax_category === "taxable" ? 10 : 0;
            return {
              category_id: categoryId,
              name: i.name,
              amount, // 税抜
              tax_category: i.tax_category,
              tax_rate: taxRate,
              tax_amount: Math.floor((amount * taxRate) / 100),
            };
          }),
      );

      // 収入で区画 (Unit) が変わった場合は付け替え: 新規作成 → 旧削除。
      // updatePayment は section 移動を扱えないため (IncomeDetailDrawer と同じ)。
      if (incomeMode) {
        const newUnitId = incomeUnitLinked ? incomeUnitId : "";
        const oldUnitId = data.unit_id || "";
        if (newUnitId !== oldUnitId) {
          const common = {
            property_id: propertyId,
            status: values.status,
            items: itemsPayload,
            title: values.title?.trim() || undefined,
            aggregation_month: values.aggregation_month || undefined,
            scheduled_date: values.scheduled_date || undefined,
            transfer_datetime: values.transfer_datetime || undefined,
            transfer_method: values.transfer_method || undefined,
            remarks: values.remarks || undefined,
          };
          if (newUnitId) {
            await dispatch(
              createPayment({ propertySectionId: newUnitId, ...common }),
            ).unwrap();
          } else {
            await dispatch(createPropertyPayment(common)).unwrap();
          }
          await dispatch(
            deletePayment({
              propertySectionId: oldUnitId || propertyId,
              paymentId,
              sort_key: data.sort_key,
            }),
          ).unwrap();
          showToast({ type: "success", message: "収入を更新しました" });
          setLocalDirty(false);
          initialItemsSerializedRef.current =
            serializeBillingItems(itemEntries);
          onUpdated?.(paymentId);
          // 全画面 (リンク先ページ) は旧 paymentId が消えるため収入タブへ戻す
          if (!embedded && propertyId) {
            navigate(`/properties/${propertyId}?tab=5&subtab=1`);
          }
          return;
        }
      }

      await dispatch(
        updatePayment({
          propertySectionId,
          paymentId,
          sort_key: data.sort_key,
          status: values.status,
          items: itemsPayload,
          // 収入軸のみタイトルを送る (契約由来の入金は触らない)
          ...(incomeMode ? { title: values.title?.trim() || undefined } : {}),
          aggregation_month: values.aggregation_month || undefined,
          scheduled_date: values.scheduled_date || undefined,
          transfer_datetime: values.transfer_datetime || undefined,
          transfer_method: values.transfer_method || undefined,
          remarks: values.remarks || undefined,
        }),
      ).unwrap();

      // 新規追加した status をサーバへ永続化 (PaymentHistoryTab と同じ遅延 upsert)
      const isDefault = DEFAULT_STATUSES.some((d) => d.value === values.status);
      const beStatuses: string[] = Array.isArray(optionRecord?.statuses)
        ? optionRecord.statuses
        : [];
      if (!isDefault && !beStatuses.includes(values.status)) {
        dispatch(
          upsertPaymentOptionManagement({
            existingId: optionRecord?.payment_option_management_id,
            statuses: [...beStatuses, values.status],
            payment_items: optionRecord?.payment_items || [],
          }),
        ).catch(() => {});
      }

      showToast({ type: "success", message: "請求を更新しました" });
      // ローカル dirty 解除 (RHF 側は reset で対応)
      reset(values);
      setLocalDirty(false);
      initialItemsSerializedRef.current = serializeBillingItems(itemEntries);
      onUpdated?.(paymentId);
    } catch (err) {
      // API のフィールド単位エラー (同一区画・同一日付の重複 → scheduled_date) は
      // 該当 input 下にインライン表示。それ以外はトースト。
      applyApiError(err, {
        setError,
        setFocus,
        onFallback: (p) =>
          showToast({
            type: "error",
            message: p.message || "更新に失敗しました",
          }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasData = Boolean(data);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasData,
    dedupeKey: `payment:${paymentId}`,
  });

  if (shouldShowInitialLoadError(initialLoadError, hasData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  if (loading || !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          minHeight: "60vh",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  const title = (() => {
    const month = data?.aggregation_month;
    if (month && /^\d{4}-\d{2}$/.test(month)) {
      return `${month.slice(0, 4)}年${month.slice(5, 7)}月請求`;
    }
    return "請求詳細";
  })();

  const formInner = (
    <Stack spacing={2}>
      {/* セクションタイトルは幅制約を外し border を 100% に (入力フィールドは 600 のまま)。
          Box で包むのは Stack の spacing がタイトルと下線(Divider)の間に入るのを防ぐため。 */}
      <Box>
        <CustomSectionTitle title="基本情報" size="small" />
      </Box>
      <Box sx={{ "& > *": { margin: "0 !important" } }}>
        <CustomSingleSelect
          label="請求年月"
          name="aggregation_month"
          control={control as any}
          options={billingMonthOptions}
          placeholder="選択してください"
          labelWidth={LABEL_WIDTH}
          inputWidth={INPUT_WIDTH}
          hasSpacing={false}
          isRequired
          onChangeValue={(val) => {
            setValue("aggregation_month", String(val || ""), {
              shouldDirty: true,
            });
          }}
        />
      </Box>

      {/* 収入文脈のみ: 場所区分 → 名称 → テナント名 (収入登録モーダルと同じカスケード・請求年月の下)。
          変更すると保存時に区画 (Unit) 紐付けを付け替える。収益カテゴリは区画から導出。 */}
      {incomeMode && (
        <Box sx={{ maxWidth: INPUT_WIDTH }}>
          <CustomCascadeSelect
            boxed
            independentModeDrafts
            title="場所・テナント選択"
            columns={[
              {
                key: "area",
                label: "場所区分",
                role: "select",
                placeholder: "場所区分を選択",
                options: incomeAreaOptions,
              },
              {
                key: "name",
                label: "名称",
                role: "select",
                placeholder: "名称を選択",
              },
              {
                key: "tenant",
                label: "テナント名",
                role: "auto",
                hint: "名称を選択すると自動で表示されます",
                emptyText: "テナント登録なし",
              },
            ]}
            rows={incomeCascadeRows}
            value={{
              area: incomeAreaType,
              name: incomeUnitId,
            }}
            onChange={(n) => {
              // カスケード = 区画への任意リンク専用。収益カテゴリは明細(科目)から決まる。
              const nextArea = n.area || "";
              const nextUnit = n.name || "";
              if (nextArea === incomeAreaType && nextUnit === incomeUnitId) {
                return;
              }
              setIncomeAreaType(nextArea);
              setIncomeUnitId(nextUnit);
              setLocalDirty(true);
            }}
            reverse={{
              key: "tenant",
              forwardLabel: "場所から探す",
              reverseLabel: "テナントから探す",
              searchPlaceholder: "テナント名で検索...",
              emptyText: "該当するテナントがありません",
            }}
            labelWidth={LABEL_WIDTH}
            inputWidth="100%"
          />
        </Box>
      )}

      {/* ステータスはセクションタイトル行の右端へ移設 (プロト準拠) */}

      <CustomCalendarInput
        name="scheduled_date"
        control={control as any}
        label="入金期日"
        labelWidth={LABEL_WIDTH}
        inputWidth={INPUT_WIDTH}
      />

      <CustomCalendarInput
        name="transfer_datetime"
        control={control as any}
        label="入金日"
        labelWidth={LABEL_WIDTH}
        inputWidth={INPUT_WIDTH}
      />

      <Box sx={{ "& > *": { margin: "0 !important" } }}>
        <CustomSingleSelect
          name="transfer_method"
          control={control as any}
          label="入金方法"
          options={TRANSFER_METHODS}
          placeholder="選択してください"
          labelWidth={LABEL_WIDTH}
          inputWidth={INPUT_WIDTH}
          hasSpacing={false}
        />
      </Box>

      {/* セクションタイトルは幅制約を外し border を 100% に (明細テーブル/合計は下で 800 に絞る)。
          Box で包むのは Stack の spacing がタイトルと下線(Divider)の間に入るのを防ぐため。 */}
      <Box>
        <CustomSectionTitle title="請求内容" size="small" />
      </Box>
      {/* 請求明細テーブル + 合計 (一番下=振込方法の下)。明細+合計のみ BILLING_WIDTH=800 に広げる。 */}
      <Box
        ref={billingTableRef}
        sx={{
          ml: { xs: 0, lg: LABEL_WIDTH },
          width: { xs: "100%", lg: BILLING_WIDTH },
          maxWidth: "100%",
        }}
      >
        <BillingItemsTable
          items={itemEntries}
          onChange={handleItemsChange}
          categoryOptions={billingCategoryOptions}
          allowAddCategory
          onAddCategory={handleAddCategory}
          revenueGroupOptions={revenueGroupOptions}
          addLabel="明細を追加"
          errorMessage={itemError ? "明細は1件以上必要です" : undefined}
          categoryRequiredErrorItemIds={categoryRequiredErrorItemIds}
        />
      </Box>
    </Stack>
  );

  // ページ直開き時のみ遷移ガード (埋め込み時はドロワー側で閉じ確認を持つ)。
  return (
    <CustomFormNavigationGuard
      hasFormChanged={!embedded && (isDirty || localDirty || itemsDirty)}
    >
      {() => (
        <Box
          pb={9}
          // 埋め込み時は他の詳細ドロワーと同じく左右合計24px (PreviewDrawer 12 + ここ 12)
          px={embedded ? 1.5 : 0}
          data-entity="payment"
          data-id={paymentId}
        >
          {/* タイトル行: 左にタイトル、右端にステータス (プロトの請求詳細ヘッダーと同じ)。
          スクロール時にタブ上部へ固定 (区画詳細 DetailPageHeader と同じ sticky パターン)。 */}
          <Box
            sx={{
              position: "sticky",
              // ドロワー(embedded)はスクロール先頭(0)、単独ページ(全画面)は固定ヘッダー(50px)の下に固定。
              top: embedded ? 0 : 50,
              zIndex: 10,
              backgroundColor: "white",
              // タイトル + 右端ステータスは行幅いっぱい (100%)。ステータスは between で右端へ。
              maxWidth: "100%",
              pt: 1,
              // 下余白(border 下の白)は持たせない。余白は本文(基本情報)側の mt で確保する。
            }}
          >
            {/* タイトル + 右端ステータス。下線(addBorder)は CustomSectionTitle 側で
            行幅いっぱいに引かれる (rightContent でステータスを内包)。 */}
            <CustomSectionTitle
              title={title}
              size="large"
              addBorder
              icon={
                !embedded ? (
                  <BackButton size={24} onClick={handleHeaderBack} />
                ) : onBack ? (
                  <BackButton size={24} onClick={onBack} />
                ) : undefined
              }
              rightContent={
                <Box
                  sx={{
                    width: 160,
                    "& .inputLabel": { display: "none" },
                    "& > *": { margin: "0 !important" },
                  }}
                >
                  <CustomCategorySelectSingle
                    label=""
                    name="status"
                    clearable={false}
                    options={statusOptions}
                    selectedValue={statusValue}
                    onValueChange={async (val) => {
                      // 必須項目。空選択 (プレースホルダー) は無視して null 更新を防ぐ。
                      if (!val || !val.trim()) return;
                      // ステータスだけは「更新」ボタンを待たず即 PATCH する (他項目は従来どおり更新ボタン)。
                      const previous = getValues("status");
                      setValue("status", val, {
                        // 即時保存するのでフォームを dirty にしない (status 変更だけで更新ボタンを点けない)。
                        shouldDirty: false,
                        shouldTouch: true,
                      });
                      if (val && val.trim()) setStatusError(false);
                      // 既存レコードのみ即時反映。新規 (未作成) は作成時にまとめて保存。
                      if (!data?.sort_key || !paymentId) return;
                      try {
                        await dispatch(
                          updatePayment({
                            propertySectionId,
                            paymentId,
                            sort_key: data.sort_key,
                            status: val,
                            ...(val === "入金済"
                              ? { transfer_datetime: new Date().toISOString() }
                              : {}),
                          }),
                        ).unwrap();
                        showToast({
                          type: "success",
                          message: "ステータスを更新しました",
                        });
                      } catch {
                        // 失敗時は元のステータスへ戻す。
                        setValue("status", previous, { shouldDirty: false });
                        showToast({
                          type: "error",
                          message: "ステータスの更新に失敗しました",
                        });
                      }
                    }}
                    onAddNewOption={handleAddNewStatus}
                    inputWidth="100%"
                    fontSize={14}
                    // トリガーは 160px と狭いので、ドロップダウンの検索+追加入力は広げる。
                    menuWidth={240}
                    placeholder="ステータス"
                    errors={
                      statusError
                        ? { status: { message: "ステータスは必須です" } }
                        : {}
                    }
                  />
                </Box>
              }
            />
          </Box>

          <Box
            component="form"
            onSubmit={onSubmit}
            // sticky タイトルとの余白は本文側の上に持たせる (タイトル側 pb だと border 下に白が出る)。
            sx={{ mt: 3 }}
            onKeyDown={(e) => {
              // Enter での暗黙送信を抑止 (更新はボタン押下時のみ)。textarea の改行は許可。
              if (
                e.key === "Enter" &&
                (e.target as HTMLElement).tagName !== "TEXTAREA"
              ) {
                e.preventDefault();
              }
            }}
          >
            {formInner}
            <Can action="payment:edit">
              <FixedActionBar
                submitLabel="更新"
                isLoading={submitting}
                disabled={!isDirty && !localDirty && !itemsDirty}
                embedded={embedded}
              />
            </Can>
          </Box>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default PaymentDetailPage;
